import { useState, useCallback } from 'react'
import { useClientSideTranslation } from 'src/services/i18n'
import { useGetCurrentLanguage } from 'src/store/game'
import { useSoundEffect } from 'src/hooks/useSoundEffect'
import { MIN_AUTO_CASHOUT_MULTIPLIER, MAX_AUTO_CASHOUT_MULTIPLIER } from '../hooks/useCrashAutoBet'

type Props = Readonly<{
  isEnabled: boolean
  isActive: boolean
  autoCashoutEnabled: boolean
  autoCashoutMultiplier: number
  onToggle: () => void
  onToggleAutoCashout: () => void
  onSetAutoCashoutMultiplier: (multiplier: number) => void
  disabled?: boolean
  // Live-round lock (v7): the bet is riding and its auto-cash-out terms were
  // submitted server-side — the cash-out toggle + stepper freeze at FULL
  // contrast (a small lock glyph marks the stepper group) while the AUTO
  // toggle stays interactive (it governs the next round's re-bet).
  cashoutLocked?: boolean
}>

export function CrashAutoBetControls({
  isEnabled,
  isActive,
  autoCashoutEnabled,
  autoCashoutMultiplier,
  onToggle,
  onToggleAutoCashout,
  onSetAutoCashoutMultiplier,
  disabled = false,
  cashoutLocked = false
}: Props) {
  const { t } = useClientSideTranslation(useGetCurrentLanguage())
  const { playEffect } = useSoundEffect()
  const [inputValue, setInputValue] = useState(autoCashoutMultiplier.toFixed(2))
  const [isInputFocused, setIsInputFocused] = useState(false)

  const handleToggleAutoBet = () => {
    if (disabled) return
    playEffect('click')
    onToggle()
  }

  const handleToggleAutoCashout = () => {
    if (disabled || cashoutLocked) return
    playEffect('click')
    onToggleAutoCashout()
  }

  const handleMultiplierChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/[^0-9.]/g, '')
    const decimalIndex = value.indexOf('.')
    if (decimalIndex !== -1) {
      value = value.slice(0, decimalIndex + 1) + value.slice(decimalIndex + 1).replace(/\./g, '')
    }
    setInputValue(value)
  }, [])

  const validateAndSetMultiplier = useCallback(
    (value: string) => {
      const numValue = parseFloat(value)
      if (isNaN(numValue) || numValue < MIN_AUTO_CASHOUT_MULTIPLIER) {
        onSetAutoCashoutMultiplier(MIN_AUTO_CASHOUT_MULTIPLIER)
        setInputValue(MIN_AUTO_CASHOUT_MULTIPLIER.toFixed(2))
      } else if (numValue > MAX_AUTO_CASHOUT_MULTIPLIER) {
        onSetAutoCashoutMultiplier(MAX_AUTO_CASHOUT_MULTIPLIER)
        setInputValue(MAX_AUTO_CASHOUT_MULTIPLIER.toFixed(2))
      } else {
        onSetAutoCashoutMultiplier(numValue)
        setInputValue(numValue.toFixed(2))
      }
    },
    [onSetAutoCashoutMultiplier]
  )

  const handleMultiplierBlur = useCallback(() => {
    setIsInputFocused(false)
    validateAndSetMultiplier(inputValue)
  }, [inputValue, validateAndSetMultiplier])

  const handleMultiplierFocus = () => {
    setIsInputFocused(true)
    setInputValue(autoCashoutMultiplier.toFixed(2))
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur()
    }
  }

  const incrementMultiplier = () => {
    if (disabled || cashoutLocked || !autoCashoutEnabled) return
    playEffect('click')
    const newValue = Math.min(MAX_AUTO_CASHOUT_MULTIPLIER, autoCashoutMultiplier + 0.1)
    onSetAutoCashoutMultiplier(newValue)
    setInputValue(newValue.toFixed(2))
  }

  const decrementMultiplier = () => {
    if (disabled || cashoutLocked || !autoCashoutEnabled) return
    playEffect('click')
    const newValue = Math.max(MIN_AUTO_CASHOUT_MULTIPLIER, autoCashoutMultiplier - 0.1)
    onSetAutoCashoutMultiplier(newValue)
    setInputValue(newValue.toFixed(2))
  }

  return (
    <div className={`crash-auto-controls ${isActive ? 'betting' : ''}`}>
      {/* Single row with everything */}
      <div className="crash-auto-controls__row">
        {/* Auto Bet Toggle */}
        <button
          className={`crash-auto-toggle ${isEnabled ? 'active' : ''}`}
          onClick={handleToggleAutoBet}
          type="button"
          disabled={disabled}
        >
          <div className={`toggle-switch ${isEnabled ? 'active' : ''}`}>
            <div className="toggle-knob" />
          </div>
          <span className="toggle-label">{t('Auto')}</span>
        </button>

        {/* Divider */}
        <div className="crash-auto-controls__divider" />

        {/* Auto Cashout Toggle — locked (full contrast, not editable) while
            the bet rides the live round: its value already went out with
            the bet. */}
        <button
          className={`crash-auto-toggle crash-auto-toggle--cashout ${autoCashoutEnabled ? 'active' : ''} ${cashoutLocked ? 'crash-auto-toggle--locked' : ''}`}
          onClick={handleToggleAutoCashout}
          type="button"
          disabled={disabled || cashoutLocked}
        >
          <div className={`toggle-switch ${autoCashoutEnabled ? 'active' : ''}`}>
            <div className="toggle-knob" />
          </div>
          <span className="toggle-label">{t('Cashout')}</span>
        </button>

        {/* Multiplier Input - always visible, disabled when cashout off */}
        <div
          className={`crash-auto-multiplier ${autoCashoutEnabled ? 'active' : ''} ${cashoutLocked ? 'crash-auto-multiplier--locked' : ''}`}
        >
          {cashoutLocked && (
            <span className="crash-auto-controls__lock" role="img" aria-label={t('Locked')}>
              <svg viewBox="0 0 12 12" aria-hidden focusable="false">
                <path
                  fillRule="evenodd"
                  d="M6 1a2.6 2.6 0 0 0-2.6 2.6V5H3a1.1 1.1 0 0 0-1.1 1.1v3.8A1.1 1.1 0 0 0 3 11h6a1.1 1.1 0 0 0 1.1-1.1V6.1A1.1 1.1 0 0 0 9 5h-.4V3.6A2.6 2.6 0 0 0 6 1Zm1.5 4V3.6a1.5 1.5 0 1 0-3 0V5h3Z"
                />
              </svg>
            </span>
          )}
          <button
            className="multiplier-btn"
            onClick={decrementMultiplier}
            type="button"
            disabled={disabled || cashoutLocked || !autoCashoutEnabled}
          >
            −
          </button>
          <input
            type="text"
            inputMode="decimal"
            className="multiplier-input"
            value={isInputFocused ? inputValue : `${autoCashoutMultiplier.toFixed(2)}x`}
            onChange={handleMultiplierChange}
            onFocus={handleMultiplierFocus}
            onBlur={handleMultiplierBlur}
            onKeyDown={handleKeyDown}
            disabled={disabled || cashoutLocked || !autoCashoutEnabled}
          />
          <button
            className="multiplier-btn"
            onClick={incrementMultiplier}
            type="button"
            disabled={disabled || cashoutLocked || !autoCashoutEnabled}
          >
            +
          </button>
        </div>
      </div>
    </div>
  )
}
